import { useState, useEffect , useRef} from "react";
import { jwtDecode } from 'jwt-decode';
import { useTranslation, Trans } from "react-i18next";
import { useSearchParams, useNavigate } from 'react-router-dom';
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import { Card, CardContent, Avatar, IconButton } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAPICall,useAppDispatch } from '../../../hooks';
import { getContent } from '../../../sdk/services/contentService';
import { clearLogin } from '../../../sdk/store/authActions';
import { ContentItem } from '../../terms/Terms';
import { getAccessToken,
  getAmwayLogoutUrlRedirect, 
  LoggerService,
  dateTimeToCustomFormat, 
  printPDF,
  PAGES, trackPage
} from '../../../ui/utils';
import { AppTokenModel } from '../../../sdk/type';
import {
  Button,
  Tag,
  TagStyle,
  Input,
  InputValidations,
  ButtonTypes,
  ButtonStyles,
  ButtonSize,
  FAIcon,
  IconStyle,
  Modal,
  Theme,
  MLPVersion,
  Search,
} from "../../../ui/components";
import {
  REACT_APP_AMWAY_REDIRECT_LOG_OUT_URI,
} from '../../../envVars';
import rewardPic from '../../../assets/image/PlaceholderReward.png';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import CampaignService from '../../../sdk/services/campaignService';
import ValidationService from '../../../sdk/services/validationService';
import RedemptionService from '../../../sdk/services/redemptionService';
import InventoryService from '../../../sdk/services/inventoryService';
import ScratchCanvas from "./ScratchCanvas";
import styles from "./index.module.scss";

const ITEMS_PER_PAGE = 4;

interface Reward {
  recId: number;
  rewardName: string;
  rewardCode: string;
  imageURL: string;
}

export default function ScratchGame() {
  const { t } = useTranslation();
  const apiCall = useAPICall();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { adminToast } = useAdminToast();

  const [orderNumber, setOrderNumber] = useState("");
  const [orderDetail, setOrderDetail] = useState<any | null>(null);
  const [redeemed, setRedeemed] = useState<any | null>(null);
  //const [locationCode, setLocationCode] = useState("");
  const [error, setError] = useState("");
  const [remaining, setRemaining] = useState(0);
  const [scratched, setScratched] = useState(0);
  const [scratchCompleted, setScratchCompleted] = useState(false);
  const [orderValid, setOrderValid] = useState(false);
  const { width, height } = useWindowSize();
  const [showConfetti, setShowConfetti] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showUpdateRewardStatus, setShowUpdateRewardStatus] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [centralisedLocation, setCentralisedLocation] = useState(false);
  const [isChallenge, setIsChallenge] = useState(false);
  const [promoChallengeId, setPromoChallengeId] = useState('');
  const [redemptionCriteria, setRedemptionCriteria] = useState('');
  const [participantName, setParticipantName] = useState('');
  const [participantSearchResults, setParticipantSearchResults] = useState<any[]>([]);
  const [selectedParticipant, setSelectedParticipant] = useState<any | null>(null);
  const [currentReward, setCurrentReward] = useState<any | null>(null);
  const [currentRewardStatus, setCurrentRewardStatus] = useState('SELECTED');
  const [lastReward, setLastReward] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [rewardPopupOpen, setRewardPopupOpen] = useState(false);
  const [campaignIconURL, setCampaignIconURL] = useState('');
  const [campaignBackgroundURL, setCampaignBackgroundURL] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleRewards, setVisibleRewards] = useState<Reward[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const campaignId = searchParams.get('id');
  const access_token = getAccessToken() || '';
  const cacheKey = 'CampaignIconURL';

  let decoded: AppTokenModel | null = access_token ? jwtDecode(access_token) : null;
  let isAdmin = decoded?.frameworkRole === 'ADMIN' ? true : false;

  const hasCompletedRef = useRef(false);
  const isVerifyingRef = useRef(false);

  useEffect(() => {
    const safeRedeemed = Array.isArray(redeemed) ? redeemed : [];
    const total = Math.ceil(safeRedeemed.length / ITEMS_PER_PAGE) || 1;
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const visible = safeRedeemed.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    setVisibleRewards(visible);
    setTotalPages(total);
  }, [redeemed, currentPage]);
  
  // useEffect(() => {
  //   hasCompletedRef.current = false;
  // }, [scratched]);

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  useEffect(() => {

    if (campaignId){
      getCampaignIcon();
      getCampaignDetail();
      const timeout = setTimeout(() => {
        trackPage(PAGES.dsw);
      }, 1000);
      return () => {
        clearTimeout(timeout);
      };

    }else{
      doLogout();
    }

  }, [campaignId]);

  useEffect(() => {
    if (rewardPopupOpen) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 6000);
      return () => clearTimeout(timer);
    }
  }, [rewardPopupOpen]);

  const getCampaignIcon = async () => {
    try {
      const data = await getContent('challenge_details', cacheKey, true);
      if (data && data.length > 0) {
        const challenge = data.find((item: ContentItem) => item.campaign_id === campaignId);
        setCampaignIconURL(challenge.challenge_icon.url || '');
        setCampaignBackgroundURL(challenge.background_image.url || '');
      }
    } catch (error) {
      LoggerService.log('Failed to retrieve content data. Using default content.');
    }
  };

  const getCampaignDetail = async () => {
    try {
      let result = await apiCall(
        CampaignService.getCampaignDetailByCampaignId,
        campaignId ?? '',
      );

      if (result?.data?.data?.campaignStart && result?.data?.data?.campaignEnd){
        const today = new Date();
        const start = new Date(result?.data?.data?.campaignStart);
        const end = new Date(result?.data?.data?.campaignEnd);
        const isWithinCampaign =  today >= start && today <= end;
        setShowInactive(!isWithinCampaign ||  result?.data?.data?.isDeleted === 1 || result?.data?.data?.campaignStatus === 'CLOSED');
        setCampaignName(result?.data?.data?.campaignName);
        setCentralisedLocation(result?.data?.data?.configJson.centralizedLocation ===1);
        setIsChallenge(result?.data?.data?.configJson?.isChallenge === 1);
        setPromoChallengeId(result?.data?.data?.configJson?.promoChallengeId || '');
        setRedemptionCriteria(result?.data?.data?.configJson?.redemptionCriteria || '');
      }
      
    } catch (error) {
      LoggerService.log('Failed to retrieve content data. Using default content.');
    }
  };

  const handleChange = (value: string) => {

    if (/^\d*$/.test(value) || value ==='') {
      setOrderNumber(value);
    }

    if (orderNumber === ''){
      setError("");
      setLoading(false);
    }
  };

  const handleVerifyOrder = async () => {
    // Prevent duplicate calls
    if (isVerifyingRef.current) {
      console.log('Verification already in progress, ignoring duplicate call');
      return;
    }

    isVerifyingRef.current = true;
    
    try {
      setError("");
      setLoading(true);

      const payload = {
        campaignId: campaignId,
        aboId: decoded?.loginId,
        isAdmin: isAdmin,
        orderNumber: orderNumber.trim(),
      };

      if (orderNumber !== '') {
        const response = await apiCall(ValidationService.validatePromoOrder, payload);
        const result = response?.data?.data[0];
        if (result.success) {
          if (result?.locationCode || (centralisedLocation && result?.locationCode === '00') ){
            setOrderValid(true);
            setOrderDetail(result);
            setRemaining(result?.chanceLeft || 0 );
            setScratched(result?.scratched);
            await getRandomReward(result?.locationCode || '0' );
          }else {
              setOrderValid(false);
              setError(t("dashboard.game.scratchCard.orderError"));
              setLoading(false);
          }
        } else {
          if (result.errorCode === 102){
            //setOrderValid(true);
            setOrderDetail(result);
            setCurrentRewardStatus(result.rewards?.[0]?.status || '');
            setRedeemed(result.rewards);
            setShowHistory(true);
          }else{
            setRemaining(0);
            setOrderValid(false);
            const errorCodeSuffix = result.errorCode ? ' (' + t("dashboard.game.scratchCard.code") + `${result.errorCode})` : ''; 
            setError(t("dashboard.game.scratchCard.invalidOrder") + errorCodeSuffix);
            setLoading(false);
          }
        }
      } else {
        setOrderValid(false);
      }
    } catch (error) {
      setOrderValid(false);
      LoggerService.log(error);
      setError(t("dashboard.game.scratchCard.orderError"));
      setLoading(false);
    } finally {
      // Always reset the ref, regardless of success or failure
      isVerifyingRef.current = false;
    }
  };

  // Challenge-mode: search participants enrolled in the linked challenge campaign by name
  const handleSearchParticipant = async (name: string) => {
    setParticipantName(name);
    setSelectedParticipant(null);
    setOrderNumber('');
    setError('');

    if (!name || name.trim().length <= 3 || !promoChallengeId) {
      setParticipantSearchResults([]);
      return;
    }

    try {
      const response = await apiCall(ValidationService.searchChallengeParticipant, promoChallengeId, name.trim());
      const results = (response?.data?.data || []).map((item: any) => ({
        ...item,
        displayName: `${item.name} (${ item.postAssessmentDate ? dateTimeToCustomFormat(item.postAssessmentDate, 'DD/MM/YYYY'): 'N/A'})`,
      }));

      if (results.length > 0 ) {
        setParticipantSearchResults(results);
      }else{
        setError(t("dashboard.game.scratchCard.participantError")) 
        setParticipantSearchResults([]);
      }
      
    } catch (searchError) {
      LoggerService.log(searchError);
      setParticipantSearchResults([]);
    }
  };

  // Challenge-mode: verify the selected participant has completed both pre- and
  // post-assessment on the linked challenge campaign, replacing order-number validation.
  const handleVerifyParticipant = async () => {
    if (isVerifyingRef.current) {
      console.log('Verification already in progress, ignoring duplicate call');
      return;
    }

    isVerifyingRef.current = true;

    try {
      setError("");
      setLoading(true);

      if (!selectedParticipant?.loginId) {
        setOrderValid(false);
        setLoading(false);
        isVerifyingRef.current = false;
        return;
      }

      const payload = {
        campaignId: campaignId,
        promoChallengeId: promoChallengeId,
        recId: selectedParticipant.recId,
        loginId: selectedParticipant.loginId,
        redemptionCriteria: redemptionCriteria,
      };

      const response = await apiCall(ValidationService.validateChallengeEntitlement, payload);
      const result = response?.data?.data[0];

      // Reuse orderNumber as the internal reference (participant enrollment recId) so the rest of
      // the scratch/claim/history flow (keyed off orderNumber) continues to work unchanged.
      setOrderNumber(selectedParticipant.recId.toString());

      if (result.success) {
        if (result?.locationCode) {
          setOrderValid(true);
          setOrderDetail(result);
          setRemaining(result?.chanceLeft || 0);
          setScratched(result?.scratched);
          await getRandomReward(result?.locationCode || '0', undefined, selectedParticipant.recId.toString());
        } else {
          setOrderValid(false);
          setError(t("dashboard.game.scratchCard.orderError"));
          setLoading(false);
        }
      } else {
        if (result.errorCode === 102) {
          setOrderDetail(result);
          setCurrentRewardStatus(result.rewards?.[0]?.status || '');
          setRedeemed(result.rewards);
          setShowHistory(true);
        } else {
          setRemaining(0);
          setOrderValid(false);
          setError(result.errorMessage || t("dashboard.game.scratchCard.invalidOrder"));
          setLoading(false);
        }
      }
    } catch (verifyError) {
      setOrderValid(false);
      LoggerService.log(verifyError);
      setError(t("dashboard.game.scratchCard.orderError"));
      setLoading(false);
    } finally {
      isVerifyingRef.current = false;
    }
  };

  // Function to get a random reward
  const getRandomReward = async (location:string, attemptNumber?: number, orderNumberOverride?: string) => {
    // Use provided attemptNumber or calculate from scratched
    const attempt = attemptNumber !== undefined ? attemptNumber : scratched + 1;
    
    const payload = {
      campaignId: campaignId,
      orderNumber: orderNumberOverride ?? orderNumber,
      attempt: attempt,
      locationCode: centralisedLocation ? '00' : location
    }
    
    //console.log(`Frontend: getRandomReward called with attempt ${attempt} (scratched=${scratched})`);
    
    const response = await apiCall(InventoryService.getRandomReward, payload);
    if (response.data?.data){
      setCurrentReward(response.data?.data);
    }else{
        setOrderValid(false);
        setRemaining(0);
        setLoading(false);
        setError(t("dashboard.game.scratchCard.rewardError"));
    }
    
  };

  const handleScratchComplete = async () => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;

    if (!currentReward) return;

    // Step 1: Claim reward
    const payload = {
      campaignId: campaignId,
      orderNumber: orderNumber,
      loginId: decoded?.loginId,
      orderQty: orderDetail.orderQty,
      orderDate: orderDetail.orderDate,
      orderAboId: orderDetail.orderAboId,
      volumeAboId: orderDetail.volumeAboId,
      orderAboName: orderDetail.orderAboName,
      volumeAboName: orderDetail.volumeAboName,
      channel: orderDetail.channel,
      recId: currentReward.recId,
      locationCode: currentReward.locationCode,
      rewardCode: currentReward.rewardCode,
      attempt: scratched + 1,
      status: 'SELECTED', 
      isDeleted: 0,
    }

    const claimRes = await apiCall(RedemptionService.claimRedemption, payload);

    if (claimRes.data.status && claimRes.data.status !== 500) {
      // Step 2: Move current reward to popup
      setLastReward(currentReward);
      setRewardPopupOpen(true);

      // Step 3: Reduce remaining chances and increment scratched
      const newRemaining = remaining - 1;
      const newScratched = scratched + 1;
      setRemaining(newRemaining);
      setScratched(newScratched);

      // Step 4: Preload next reward IMMEDIATELY (if chances remain)
      if (newRemaining > 0 && !scratchCompleted) {
        // Use newScratched + 1 for next attempt number
        await getRandomReward(orderDetail.locationCode, newScratched + 1);
        hasCompletedRef.current = false;
      }

      // Step 5: If no more chances, mark completed
      if (newRemaining === 0) {
        const rewards = await apiCall(RedemptionService.getRedemptionRewardByOrder, {
          campaignId: campaignId,
          orderNumber: orderNumber,
        });
        setScratchCompleted(true);
        setCurrentRewardStatus('SELECTED');
        setRedeemed(rewards.data.data);
        setShowHistory(true);
        setOrderNumber("");
        hasCompletedRef.current = false; 
      }
    } else {
      setError("Reward claim failed. Please try again.");
      setRemaining(0);
      setLoading(false);
      hasCompletedRef.current = false;
    }
  };

  const updateRewardStatus = async () => {

  const recIds = (orderDetail?.rewards ?? redeemed)
    .flatMap((r: { recIds: string }) => (r.recIds || '').split(','))
    .map((id: string) => id.trim())
    .filter(Boolean);

    try {

      const payload = {
        status: 'DISTRIBUTED',
        campaignId,
        orderNumber: orderDetail?.orderNumber,
        recIds: recIds,
        locationCode: orderDetail?.locationCode,
        rewards: orderDetail.rewards,
      };

      const result = await apiCall(RedemptionService.updateRedemption, payload);

      if (result.status === 200 && result?.data?.status) {
        adminToast({
          message: result?.data?.message,
          theme: Theme.SUCCESS,
        });

        setCurrentRewardStatus('DISTRIBUTED');
      } else {
        adminToast({
          message: result?.data?.message,
          theme: Theme.ERROR,
        });
      }
    } catch (err: any) {
      adminToast({
        message: err.message || 'Error updating reward',
        theme: Theme.ERROR,
      });
    } finally {
      setShowUpdateRewardStatus(false);
    }
  };


  const handlePrint = () => {

    if (redeemed && orderDetail){
      let lines;
      const start = dateTimeToCustomFormat(redeemed[0].campaignStart, 'DD MMM YYYY');
      const end = dateTimeToCustomFormat(redeemed[0].campaignEnd, 'DD MMM YYYY');
      const headers = [t('admin.redemption.invoice.header') + redeemed[0].campaignName, 
        t('admin.redemption.invoice.header2') + start + ' - ' + end]
      if (isChallenge){
        lines = [ t('admin.redemption.invoice.name') +  orderDetail.volumeAboId + ' ' + orderDetail.volumeAboName, 
        t('admin.redemption.invoice.enrollmentID') +  orderDetail.orderNumber, ]
      }else{
        lines = [t('admin.redemption.invoice.orderingABO') +  orderDetail.orderAboId + ' ' + orderDetail.orderAboName, 
        t('admin.redemption.invoice.orderForABO') +  orderDetail.volumeAboId + ' ' + orderDetail.volumeAboName, 
        t('admin.redemption.invoice.orderNumber') +  orderDetail.orderNumber, ]
      }

      const columns = [t('admin.redemption.invoice.number'), t('admin.redemption.invoice.sku'), t('admin.redemption.invoice.reward'), t('admin.redemption.invoice.quantity')];
      const rows = redeemed.map((r: { rewardCode: string; rewardName: string; quantity: number }, i: number) => [
        i + 1,
        r.rewardCode,
        r.rewardName,
        r.quantity,
      ]);
      printPDF(
          headers,
          lines,
          columns,
          false,
          rows,
          { size: "half-A4-horizontal", orientation: "l" }
        );
    }else{
      adminToast({
        message: t('admin.redemption.invoice.emptyMessage'),
        theme: Theme.ERROR,
      });
    }
    
  };

  // When popup closes, just close it - don't preload next reward
  const handleClosePopup = () => {
    setRewardPopupOpen(false);
    // Removed preloading - already done in handleScratchComplete
  };

  const handleCloseInactivePopup = () => {
    setShowInactive(false);
    if (decoded?.frameworkRole === 'ADMIN'){
      navigate(-1)
    }else{
      doLogout();
    }
    
  };

  const getInitials = (name: string) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const logoutFn = (): Function => {
    const amwayIdSignOut = () => {
      //window.location.href = getAmwayLogoutUrlRedirect(REACT_APP_AMWAY_REDIRECT_LOG_OUT_URI + location.pathname);
      window.location.href = getAmwayLogoutUrlRedirect(REACT_APP_AMWAY_REDIRECT_LOG_OUT_URI);
    };

    return () => {
      amwayIdSignOut();
    };
  };
  

  const doLogout = () => {
    localStorage.removeItem('pinNumber');
    localStorage.removeItem('appToken');
    localStorage.removeItem('loginToken');
    localStorage.removeItem('exp');
    localStorage.removeItem('userId');
    localStorage.removeItem('loginId');
    dispatch(clearLogin(logoutFn()));
    //navigate('/');
  };

  const updateRewardStatusPrompt = (
    <>
      {showUpdateRewardStatus && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('dashboard.game.scratchCard.redeemMessage')}`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('dashboard.game.scratchCard.btnRedeem')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                updateRewardStatus();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('dashboard.game.scratchCard.back')}
              type={ButtonTypes.BUTTON}
              onClick={() => setShowUpdateRewardStatus(false)}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.BLACK}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  const rewardPopup = (
    <>
      {rewardPopupOpen && (
        <Modal
          primaryButtonLabel={''}
          headline={''}
          closeIcon={false}
          containerWidth={'400px'}
          primaryButton={
            <div className={styles['button-container']}>
              <Button
                title={t("dashboard.game.scratchCard.close")}
                onClick={() => handleClosePopup()}
                size={ButtonSize.SMALL}
              />
            </div>
          }
        >
          <div className={styles['popup']}>
            <span className={styles.rewardTitle}>{t("dashboard.game.scratchCard.popup.title")}</span>
              <p>
                 <Trans
                i18nKey="dashboard.game.scratchCard.popup.message"
                values={{ reward: lastReward.rewardName }}
                shouldUnescape
                components={[<span className={styles.rewardName} />]}
              />
              </p>
              <img src={lastReward.imageURL} alt="Reward" style={{ maxWidth: "100%" }} />
          </div>
        </Modal>
      )}
    </>
  );

    const inactivePopup = (
    <>
      {showInactive && (
        <Modal
          primaryButtonLabel={''}
          headline={''}
          closeIcon={false}
          containerWidth={'400px'}
          primaryButton={
            <div className={styles['button-container']}>
              <Button
                title={t("dashboard.game.scratchCard.close")}
                onClick={() => handleCloseInactivePopup()}
                size={ButtonSize.SMALL}
              />
            </div>
          }
        >
          <div className={styles['popup']}>
            <span className={styles.rewardTitle}>{t("dashboard.game.scratchCard.inactivePopup.title")}</span>
              <p>
                 <Trans
                i18nKey="dashboard.game.scratchCard.inactivePopup.message"
                values={{ campaignName: campaignName }}
                shouldUnescape={true}
                components={[<span className={styles.rewardName} />]}
              />
              </p>
          </div>
        </Modal>
      )}
    </>
  );

 return (
  <div className={styles.pageWrapper}>
    {campaignBackgroundURL && (
      <div
        className={styles.overlayBackground}
        style={{ backgroundImage: `url(${campaignBackgroundURL})` }}
      />
    )}

    {!isAdmin && (
      <div className={styles.header}>
        <div className={styles.userInfo}>
          <Avatar className={styles.avatar}>
            {getInitials(decoded?.name || "A")}
          </Avatar>
          <span className={styles.userName}>{decoded?.name || 'Anonymous'}</span>
        </div>
        <IconButton className={styles.userName}
          aria-label="logout"
          onClick={doLogout}
        >
          <LogoutIcon />
        </IconButton>
      </div>
    )}
    
    <div className={styles.pageContainer}>
    { (
      <div className={styles.campaignLogo}>
        <img
          src={campaignIconURL}
          alt="campaignLogo"
          className={styles.campaignImage}
        />
      </div>
    )}

      {!showHistory ? (
        <>
        <div className={styles.panel}>
            <span className={styles.label}>
              {isChallenge
                ? t('dashboard.game.scratchCard.verifyParticipant')
                : t('dashboard.game.scratchCard.verifyOrder')}
            </span>

            {isChallenge ? (
              <div className={styles.inputContainer}>
                <Search
                  name="participantName"
                  value={participantName}
                  mlpVersion={MLPVersion.ONE}
                  className={styles.searchInput}
                  placeholder={t('dashboard.game.scratchCard.participantNamePlaceholder')}
                  disabled={remaining > 0}
                  data={participantSearchResults}
                  configData={{
                    label: 'displayName',
                    onSelect: (item: any) => {
                      setSelectedParticipant(item);
                      setParticipantName(item.displayName);
                      setParticipantSearchResults([]);
                      setError('');
                    },
                  }}
                  onChange={(e) => handleSearchParticipant(e.target.value)}
                  onSearch={() => handleSearchParticipant(participantName)}
                  onClear={() => {
                    setParticipantName('');
                    setSelectedParticipant(null);
                    setParticipantSearchResults([]);
                    setOrderNumber('');
                    setError('');
                  }}
                  message={
                    participantName !== ''
                      ? orderValid
                        ? ''
                        : error
                          ? error
                          : undefined
                      : t('dashboard.game.scratchCard.emptyParticipant')
                  }
                />
              </div>
            ) : (
              <div className={styles.inputContainer}>
                <Input
                  name="orderNumber"
                  value={orderNumber}
                  mlpVersion={MLPVersion.ONE}
                  className={styles.input}
                  onChange={(e) => handleChange(e.target.value.trim())}
                  disabled={remaining > 0}
                  message={
                    orderNumber !== ''
                      ? orderValid
                        ? ""
                        : error
                          ? error
                          : null
                      : t("dashboard.game.scratchCard.emptyOrder")
                  }
                  status={
                    orderNumber !== ''
                      ? orderValid
                        ? InputValidations.VALID
                        : error
                          ? InputValidations.WARNING
                          : InputValidations.DEFAULT
                      : InputValidations.DEFAULT
                  }
                />
                {orderNumber.length > 0 && remaining <= 0 && (
                  <FAIcon
                    className={error ? styles['close-icon-err'] : styles['close-icon']}
                    iconStyle={IconStyle.SOLID}
                    data-testid="close-icon-test-id"
                    iconName="close"
                    theme={Theme.BLACK}
                    onClick={() => handleChange('')}
                  />
                )}
              </div>
            )}
            {orderValid && orderNumber && (<span className={styles.name}>{orderDetail.volumeAboName + ' ' + orderDetail.volumeAboId}</span>)}
            {remaining === 0 && (
              <Button
                title={
                  loading && !error && (isChallenge ? selectedParticipant : orderNumber)
                    ? t("dashboard.game.scratchCard.buttonMsg")
                    : t("dashboard.game.scratchCard.button")
                }
                type={ButtonTypes.BUTTON}
                buttonStyle={ButtonStyles.SOLID}
                size={ButtonSize.SMALL}
                theme={Theme.BLACK}
                onClick={isChallenge ? handleVerifyParticipant : handleVerifyOrder}
                disabled={loading || (isChallenge ? !selectedParticipant : orderNumber === '')}
              />
            )}

            {remaining > 0 && (
              <div>
                <p className={styles.scratchMessage}>{t("dashboard.game.scratchCard.scratchMessage")}</p>
                <Card className={styles.scratchCard}>
                  <CardContent className={styles.scratchCardContent}>
                    <ScratchCanvas
                      key={scratched}
                      width={288}
                      height={288}
                      finishPercent={10}
                      coverColor="#e0e0e0"
                      onComplete={handleScratchComplete}
                      reward={
                        <img
                          src={rewardPic}
                          alt="Reward"
                          className={styles.rewardImage}
                        />
                      }
                    />
                  </CardContent>
                </Card>
              </div>
            )}

            {remaining > 0 && (
              <p className={styles.remainingCounter}>
                <Trans
                  i18nKey="dashboard.game.scratchCard.remainingMessage"
                  values={{ chances: remaining }}
                  components={[<span className={styles.remainingNumber} />]}
                />
              </p>
            )}
          </div>
        </>
       ) : (

          <div className={styles["reward-history"]}>
            <div className={styles.buttonRow}>
              <button
                className={styles["back-button"]}
                onClick={() => {
                  setShowHistory(false);
                  setOrderNumber('');
                  setOrderValid(false);
                  setOrderDetail(null);
                  setRemaining(0);
                  setScratched(0);
                  setCurrentReward(null);
                  setRedeemed(null);
                  setError('');
                  setLoading(false);
                  setScratchCompleted(false);
                }}
              >
                <i className="fa-solid fa-chevron-left"></i>
                  &nbsp;{t("dashboard.game.scratchCard.back")}
              </button>
              {isAdmin && (
                <button
                  className={styles["print-button"]}
                  onClick={() => {
                    handlePrint();
                  }}
                >
                  <i className="fa fa-print"></i>
                  &nbsp;{t("dashboard.game.scratchCard.print")}
                </button>
              )}

            </div>

            <div className={styles.card}>
              <div className={styles.orderHeader}>
                <h3>
                  {isChallenge
                    ? t("dashboard.game.scratchCard.order.participantDetail")
                    : t("dashboard.game.scratchCard.order.orderDetail")}
                </h3>
                 <Tag
                      text={currentRewardStatus === 'SELECTED' ?  t("dashboard.game.scratchCard.order.notRedeemed") : 
                        currentRewardStatus === 'DISTRIBUTED' ?  t("dashboard.game.scratchCard.order.redeemed") : currentRewardStatus}
                      style={TagStyle.OUTLINE}
                      theme={
                        currentRewardStatus === 'DISTRIBUTED'
                          ? Theme.SUCCESS
                          : currentRewardStatus === 'SELECTED'
                            ? Theme.WARNING
                            : Theme.ERROR
                      }
                      className={styles['status-tag']}
                  />
              </div>

              <div className={styles.divider}></div>

              <div className={styles.content}>
                <div className={styles.row}>
                  <span className={styles.label}>
                    {isChallenge
                      ? t("dashboard.game.scratchCard.order.name")
                      : t("dashboard.game.scratchCard.order.orderingAbo")}
                  </span>
                  <span className={styles.value}>
                    {orderDetail.orderAboId + ' ' + orderDetail.orderAboName}
                  </span>
                </div>

                {isChallenge ? (
                  <>
                    <div className={styles.row}>
                      <span className={styles.label}>{t("dashboard.game.scratchCard.order.teamName")}</span>
                      <span className={styles.value}>{orderDetail.teamName}</span>
                    </div>

                    <div className={styles.row}>
                      <span className={styles.label}>{t("dashboard.game.scratchCard.order.teamPeriod")}</span>
                      <span className={styles.value}>
                        {orderDetail.teamStartDate ? dateTimeToCustomFormat(orderDetail.teamStartDate, 'DD MMM YYYY') : ''}
                        {' - '}
                        {orderDetail.teamEndDate ? dateTimeToCustomFormat(orderDetail.teamEndDate, 'DD MMM YYYY') : ''}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.row}>
                      <span className={styles.label}>{t("dashboard.game.scratchCard.order.orderForABO")}</span>
                      <span className={styles.value}>{orderDetail.volumeAboId + ' ' + orderDetail.volumeAboName}</span>
                    </div>

                    <div className={styles.row}>
                      <span className={styles.label}>{t("dashboard.game.scratchCard.order.orderNumber")}</span>
                      <span className={styles.value}>{orderDetail.orderNumber}</span>
                    </div>
                  </>
                )}

              </div>


              {isAdmin && currentRewardStatus === 'SELECTED' && (
                <div className={styles.btnRedeem}>
                <Button
                  title={ t("dashboard.game.scratchCard.btnRedeem")}
                  type={ButtonTypes.BUTTON}
                  buttonStyle={ButtonStyles.SOLID}
                  size={ButtonSize.SMALL}
                  theme={Theme.BLACK}
                  onClick={() => setShowUpdateRewardStatus(true)}
                />
              </div>
              )}
              
            </div>

            <div className={styles["reward-table-container"]}>
              <div className={styles["table-header"]}>
                <div>{t("dashboard.game.scratchCard.gift")}</div>
                <div>{t("dashboard.game.scratchCard.quantity")}</div>
                {/* <div>{t("dashboard.game.scratchCard.claimDate")}</div> */}
              </div>

              {/* Desktop Table Rows */}
              {visibleRewards.map((reward:any) => (
                <div key={reward.id} className={styles["table-row"]}>
                  <div className={styles["table-item"]}>
                    <img
                      src={reward.imageURL}
                      alt={reward.rewardName}
                      className={styles["reward-image"]}
                    />
                    <div className={styles["reward-info"]}>
                      <div className={styles["reward-name"]}>{reward.rewardName}</div>
                      <div className={styles["reward-product"]}>
                        {t("dashboard.game.scratchCard.product")} #{reward.rewardCode}
                      </div>
                    </div>
                  </div>
                  <div className={styles["table-item"]}>{reward.quantity}</div>
                  {/* <div className={styles["table-item"]}>{reward.createdDate.split(' ')[0]}</div> */}
                </div>
              ))}

              {/* Mobile Card Layout */}
              <div className={styles["mobile-list"]}>
                {visibleRewards.map((reward:any) => (
                  <div key={reward.id} className={styles["mobile-card"]}>
                    <div className={styles["card-left"]}>
                      <img
                        src={reward.imageURL}
                        alt={reward.rewardName}
                        className={styles["reward-image"]}
                      />
                      <div>
                        <div className={styles["reward-name"]}>{reward.rewardName}</div>
                        <div className={styles["reward-product"]}>
                          {t("dashboard.game.scratchCard.product")} #{reward.rewardCode}
                        </div>
                        <div className={styles["reward-product"]}>
                          {t("dashboard.game.scratchCard.quantity")} x{reward.quantity}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  onClick={handlePrev}
                  disabled={currentPage === 1}
                  className={styles.pageButton}
                >
                 <i className="fa-solid fa-chevron-left"></i>
                </button>
                <span className={styles.pageInfo}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                  className={styles.pageButton}
                >
                  <i className="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            )}
            </div>
          </div>
       
        )}

      {rewardPopup}
      {inactivePopup}
      {updateRewardStatusPrompt}

      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          numberOfPieces={700}
          recycle={false}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 2000,
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  </div>
);
}
